import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Mail } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('no-token'); return; }
    api.get(`/auth/verify-email?token=${token}`)
      .then(() => setStatus('success'))
      .catch(err => { setStatus('error'); setError(err.response?.data?.error || 'Verification failed'); });
  }, [token]);

  const resend = async () => {
    setResending(true);
    try {
      await api.post('/auth/send-verification');
      setResent(true);
    } catch {
      // silent
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <>
            <Loader className="w-12 h-12 text-green-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Verifying your email...</p>
          </>
        )}

        {status === 'success' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
            <div className="w-16 h-16 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-black text-white mb-2">Email verified! ✅</h1>
            <p className="text-gray-400 mb-6">Your email address has been confirmed. Your account is fully active.</p>
            <Link to="/dashboard" className="inline-block bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-6 py-3 rounded-xl transition-colors">
              Go to Dashboard →
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
            <div className="w-16 h-16 bg-red-500/10 border-2 border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-black text-white mb-2">Link expired</h1>
            <p className="text-gray-400 mb-6">{error || 'This verification link is invalid or has expired.'}</p>
            {user && !resent && (
              <button
                onClick={resend}
                disabled={resending}
                className="bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-6 py-3 rounded-xl transition-colors"
              >
                {resending ? 'Sending...' : 'Send new verification email'}
              </button>
            )}
            {resent && <p className="text-green-400 text-sm">New verification email sent! Check your inbox.</p>}
          </div>
        )}

        {status === 'no-token' && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
            <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h1 className="text-xl font-black text-white mb-2">Check your email</h1>
            <p className="text-gray-400 mb-4">A verification link was sent to your email when you registered. Click the link in that email to verify your account.</p>
            {user && (
              <button onClick={resend} disabled={resending || resent} className="text-green-400 hover:text-green-300 text-sm transition-colors">
                {resent ? '✓ Sent!' : resending ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
          </div>
        )}

        <Link to="/" className="block text-gray-500 hover:text-gray-300 text-sm mt-6 transition-colors">← Back to PlarixMC</Link>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
